import { useState } from "react"

// A list of scan targets ("email me at 100, 500 and 1,000 scans") as chips,
// with one number field to add another (#304). Used on the account default
// in Profile, on a group and on a code.
//
// The limits match the server's (ScanAlerts::Thresholds): whole numbers from
// 1 to 10,000,000, ten at most. They are abuse guards, so the copy never says
// "upgrade". The server checks again and its message wins.
export const MAX_TARGETS = 10
const MAX_VALUE = 10_000_000

export function formatCount(n) {
  return Number(n).toLocaleString("en-US")
}

export default function ThresholdEditor({ id, values, onChange, disabled }) {
  const [draft, setDraft] = useState("")
  const [error, setError] = useState(null)

  function add(e) {
    e.preventDefault()
    const n = Number(draft.replace(/[,\s_]/g, ""))
    if (!Number.isInteger(n) || n < 1 || n > MAX_VALUE) {
      setError(`A scan target is a whole number from 1 to ${formatCount(MAX_VALUE)}.`)
      return
    }
    if (values.includes(n)) { setDraft(""); setError(null); return }
    if (values.length >= MAX_TARGETS) {
      setError(`Use ${MAX_TARGETS} scan targets or fewer.`)
      return
    }
    setError(null)
    setDraft("")
    onChange([...values, n].sort((a, b) => a - b))
  }

  function remove(n) {
    setError(null)
    onChange(values.filter((v) => v !== n))
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        {values.map((n) => (
          <span key={n} className="inline-flex items-center gap-1 rounded-full border border-[color:var(--color-border)] dark:border-storm pl-2.5 pr-1 py-0.5 font-mono text-xs text-ink dark:text-cloud">
            {formatCount(n)}
            <button
              type="button"
              onClick={() => remove(n)}
              disabled={disabled}
              aria-label={`Remove the ${formatCount(n)} scan target`}
              className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[color:var(--color-muted)] dark:text-muted-light hover:text-fail bg-transparent border-0 cursor-pointer disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-voltage"
            >
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </span>
        ))}
        {values.length === 0 && (
          <span className="text-xs text-[color:var(--color-muted)] dark:text-muted-light">No scan targets.</span>
        )}
      </div>
      <form onSubmit={add} className="flex gap-2 mt-2">
        <label htmlFor={id} className="sr-only">Add a scan target</label>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. 500"
          disabled={disabled}
          className="w-32 border border-[color:var(--color-border)] dark:border-storm rounded px-2.5 py-1.5 text-sm font-mono bg-white dark:bg-midnight text-ink dark:text-cloud focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 dark:focus-visible:ring-offset-midnight"
        />
        <button
          type="submit"
          disabled={disabled || !draft.trim()}
          className="border border-[color:var(--color-border)] dark:border-storm text-ink dark:text-cloud rounded-lg px-3 py-1.5 text-sm font-medium hover:border-voltage transition disabled:opacity-50"
        >
          Add
        </button>
      </form>
      {error && <p role="alert" className="text-xs text-fail mt-1.5">{error}</p>}
    </div>
  )
}
